import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subscribeToAuthChanges } from '@services/auth';
import { useAuthStore } from '@store/authStore';
import { useTradeStore } from '@store/tradeStore';
import { useStrategyStore } from '@store/strategyStore';
import { useAdminStore } from '@store/adminStore';
import { useSettingsStore } from '@store/settingsStore';
import { Strategy } from './types';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@services/firebase';
import { trackSession } from '@services/analytics';

// Move these down below lazyWithRetry definition

// Custom Lazy wrapper to catch chunk loading errors (e.g., after a new deployment) and force a page reload
const lazyWithRetry = (componentImport: () => Promise<any>) =>
    React.lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Assuming that the user is not on the latest version, let's refresh the page immediately.
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                return window.location.reload();
            }

            // The page has already been reloaded
            // Assuming that user is already using the latest version of the application.
            // Let's let the application crash and raise the error.
            throw error;
        }
    });

// Layouts and Landing Lazy
const MainLayout = lazyWithRetry(() => import('@components/layouts/MainLayout'));
const AuthLayout = lazyWithRetry(() => import('@components/layouts/AuthLayout'));
const LandingPage = lazyWithRetry(() => import('@pages/LandingPage'));

// Pages Lazy
const Dashboard = lazyWithRetry(() => import('@pages/Dashboard'));
const Workspace = lazyWithRetry(() => import('@pages/Workspace'));
const Fundamental = lazyWithRetry(() => import('@pages/Fundamental'));
const StrategyHub = lazyWithRetry(() => import('@pages/StrategyHub'));
const Subscriptions = lazyWithRetry(() => import('@pages/subscriptions/Subscriptions'));
const Settings = lazyWithRetry(() => import('@pages/Settings'));
const Metrics = lazyWithRetry(() => import('@pages/Metrics'));
const AIAudit = lazyWithRetry(() => import('@pages/AIAudit'));
const Journal = lazyWithRetry(() => import('@pages/Journal'));
const TradingFloor = lazyWithRetry(() => import('@pages/TradingFloor'));
const Login = lazyWithRetry(() => import('@pages/auth/Login'));
const Register = lazyWithRetry(() => import('@pages/auth/Register'));
const ForgotPassword = lazyWithRetry(() => import('@pages/auth/ForgotPassword'));

// Blog Public Lazy
const BlogList = lazyWithRetry(() => import('@pages/BlogList'));
const BlogDetail = lazyWithRetry(() => import('@pages/BlogDetail'));

// Support & Legal Lazy
const FAQ = lazyWithRetry(() => import('@pages/FAQ'));
const Legal = lazyWithRetry(() => import('@pages/Legal'));
const Contact = lazyWithRetry(() => import('@pages/Contact'));
const Features = lazyWithRetry(() => import('@pages/Features'));
const Glossary = lazyWithRetry(() => import('@pages/Glossary'));
const PublicTrade = lazyWithRetry(() => import('@pages/PublicTrade'));

// Admin Pages Lazy
const AdminDashboard = lazyWithRetry(() => import('@pages/admin/AdminDashboard'));
const SmartAnalysis = lazyWithRetry(() => import('@pages/admin/SmartAnalysis'));
const Maintenance = lazyWithRetry(() => import('@pages/Maintenance'));

// Options Lazy
const OptionsDesk = lazyWithRetry(() => import('@pages/options/OptionsDesk'));

import ScrollToTop from '@components/ui/ScrollToTop';

const LanguageSync: React.FC = () => {
    const { lang } = useParams<{ lang: string }>();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Track the last processed state to identify source of change
    const [initialRender, setInitialRender] = React.useState(true);
    const lastLangParam = React.useRef<string | undefined>(lang);
    const lastI18nLang = React.useRef<string | undefined>(i18n.language?.split('-')[0] || 'fr');

    const validLangs = ['en', 'es', 'pt', 'de', 'it', 'fr'];

    useEffect(() => {
        // 1. Handle legacy query parameters (?lng=en or ?lang=en)
        const params = new URLSearchParams(window.location.search);
        const queryLang = params.get('lng') || params.get('lang');

        if (queryLang && validLangs.includes(queryLang)) {
            const cleanPath = location.pathname;
            const targetPath = queryLang === 'fr' ? cleanPath : `/${queryLang}${cleanPath}`;
            i18n.changeLanguage(queryLang);
            navigate(targetPath, { replace: true });
            return;
        }

        const currentI18nLang = i18n.language?.split('-')[0] || 'fr';

        // 2. URL Path Parameter changed -> Update i18n
        if (initialRender || lang !== lastLangParam.current) {
            if (lang && validLangs.includes(lang)) {
                if (currentI18nLang !== lang) {
                    i18n.changeLanguage(lang);
                }
            } else if (!lang && currentI18nLang !== 'fr') {
                // If we navigate to a root path (no prefix), ensure i18n is FR
                i18n.changeLanguage('fr');
            }
            lastLangParam.current = lang;
            lastI18nLang.current = i18n.language?.split('-')[0] || 'fr';
        }
        // 3. i18n Language changed manually (e.g. from flag buttons) -> Update URL
        else if (currentI18nLang !== lastI18nLang.current) {
            if (validLangs.includes(currentI18nLang)) {
                const pathParts = location.pathname.split('/').filter(Boolean);
                const firstPart = pathParts[0];
                const hasLangPrefix = firstPart && validLangs.includes(firstPart);
                const actualPathParts = hasLangPrefix ? pathParts.slice(1) : pathParts;
                const subPath = '/' + actualPathParts.join('/');
                const newPath = currentI18nLang === 'fr' ? subPath : `/${currentI18nLang}${subPath}`;

                if (newPath !== location.pathname) {
                    navigate(newPath, { replace: true });
                }
            }
            lastI18nLang.current = currentI18nLang;
        }

        if (initialRender) {
            setInitialRender(false);
        }
    }, [lang, i18n.language, navigate, location.pathname, initialRender]);

    return null;
};

const App: React.FC = () => {
    const { i18n } = useTranslation();
    const { user, loading, setUser } = useAuthStore();
    const { setTrades } = useTradeStore();
    const { setStrategies, setTopStrategyIds } = useStrategyStore();
    const { setPendingUsersCount, setPendingContentCount, setPendingMessagesCount, setPendingPublicTradesCount } = useAdminStore();
    const { fetchSettings, settings } = useSettingsStore();

    // ÉCOUTEUR GLOBAL DES RÉGLAGES SITE
    useEffect(() => {
        const unsubscribe = fetchSettings();

        // Timeout to prevent analytics from blocking critical rendering path
        const timer = setTimeout(() => {
            trackSession();
        }, 5000);

        return () => {
            clearTimeout(timer);
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [fetchSettings]);

    // ÉCOUTEUR GLOBAL DU CLASSEMENT HUB
    useEffect(() => {
        if (!user) return; // Ne pas écouter si non connecté

        const minTrades = settings?.minTradesForHub || 10;
        const q = query(
            collection(db, 'strategies'),
            where('totalTrades', '>=', minTrades)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetched = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Strategy))
                .filter(s => s.authorStatus !== 'BANNED');

            // Tri multi-niveaux identique au Hub
            const sorted = fetched.sort((a, b) => {
                const votesDiff = (b.votes || 0) - (a.votes || 0);
                if (votesDiff !== 0) return votesDiff;

                const winRateDiff = (b.winRate || 0) - (a.winRate || 0);
                if (winRateDiff !== 0) return winRateDiff;

                const rrDiff = (b.avgRR || 0) - (a.avgRR || 0);
                if (rrDiff !== 0) return rrDiff;

                const dateA = a.createdAt ? (a.createdAt as any).seconds || new Date(a.createdAt as any).getTime() : 0;
                const dateB = b.createdAt ? (b.createdAt as any).seconds || new Date(b.createdAt as any).getTime() : 0;
                return dateB - dateA;
            });

            const ids = sorted.slice(0, 3).map(s => s.id);
            setTopStrategyIds(ids);
        }, (error) => {
            console.error("Erreur classement Hub:", error);
        });

        return () => unsubscribe();
    }, [setTopStrategyIds, settings, user?.id]);

    // Observer l'état de l'authentification Firebase
    useEffect(() => {
        const unsubscribe = subscribeToAuthChanges((u) => {
            setUser(u);
        });

        return () => unsubscribe();
    }, [setUser]);

    // HEARTBEAT: Mettre à jour lastActiveAt toutes les 2 minutes pour le statut en ligne
    useEffect(() => {
        if (!user) return;

        const updateActivity = async () => {
            try {
                const userDocRef = doc(db, 'users', user.id);
                await updateDoc(userDocRef, {
                    'metadata.usage.lastActiveAt': serverTimestamp()
                });
            } catch (err) {
                // Silently fail if there's a permission or network issue
                console.warn("Heartbeat update failed:", err);
            }
        };

        // Premiere mise à jour immédiate
        updateActivity();

        // Puis toutes les 2 minutes
        const intervalId = setInterval(updateActivity, 2 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [user?.id]);

    const getTime = (val: any) => {
        if (!val) return 0;
        if (val.seconds) return val.seconds * 1000;
        if (val instanceof Date) return val.getTime();
        return new Date(val).getTime() || 0;
    };

    // ÉCOUTEUR GLOBAL DES TRADES
    useEffect(() => {
        if (!user) {
            setTrades([]);
            return;
        }

        const q = query(
            collection(db, 'trades'),
            where('userId', '==', user.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedTrades = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            })) as any[];

            const sorted = fetchedTrades.sort((a, b) => getTime(b.entryDate) - getTime(a.entryDate));
            setTrades(sorted);
        }, (error) => {
            console.error("Erreur synchronisation trades:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setTrades]);

    // ÉCOUTEUR GLOBAL DES STRATÉGIES
    useEffect(() => {
        if (!user) {
            setStrategies([]);
            return;
        }

        const q = query(
            collection(db, 'strategies'),
            where('userId', '==', user.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedStrategies = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            })) as Strategy[];

            const sorted = fetchedStrategies.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

            setStrategies(sorted);
        }, (error) => {
            console.error("Erreur synchronisation stratégies:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setStrategies]);

    // ÉCOUTEUR GLOBAL ADMIN (Profils à checker)
    useEffect(() => {
        if (!user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
            setPendingUsersCount(0);
            return;
        }

        const q = query(
            collection(db, 'users'),
            where('isAdminChecked', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingUsersCount(snapshot.size);
        }, (error) => {
            console.error("Erreur synchronisation notifications admin:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setPendingUsersCount]);

    // ÉCOUTEUR GLOBAL ADMIN (Messages de Contact)
    useEffect(() => {
        if (!user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
            setPendingMessagesCount(0);
            return;
        }

        const q = query(
            collection(db, 'contact_messages'),
            where('status', '==', 'PENDING')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingMessagesCount(snapshot.size);
        }, (error) => {
            console.error("Erreur synchronisation notifications admin messages:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setPendingMessagesCount]);

    // ÉCOUTEUR GLOBAL ADMIN (Contenu à checker)
    useEffect(() => {
        if (!user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
            setPendingContentCount(0);
            return;
        }

        const q = query(
            collection(db, 'strategies'),
            where('isAdminChecked', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingContentCount(snapshot.size);
        }, (error) => {
            console.error("Erreur synchronisation notifications admin contenu:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setPendingContentCount]);

    // ÉCOUTEUR GLOBAL ADMIN (Trades Publics à checker)
    useEffect(() => {
        if (!user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
            setPendingPublicTradesCount(0);
            return;
        }

        const q = query(
            collection(db, 'trades'),
            where('isPublic', '==', true),
            where('isAdminChecked', '==', false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingPublicTradesCount(snapshot.size);
        }, (error) => {
            console.error("Erreur synchronisation notifications admin trades publics:", error);
        });

        return () => unsubscribe();
    }, [user?.id, setPendingPublicTradesCount]);

    // ÉCOUTEUR GLOBAL POUR LA LANGUE (Sync Profil -> i18n)
    useEffect(() => {
        if (user?.preferredLanguage) {
            if (!i18n.language.startsWith(user.preferredLanguage)) {
                i18n.changeLanguage(user.preferredLanguage);
            }
        }
    }, [user?.preferredLanguage]);

    const isMaintenance = settings?.maintenanceMode && user?.role !== 'ADMIN';
    const isAuthRoute = window.location.pathname === '/login' ||
        window.location.pathname === '/register' ||
        window.location.pathname === '/forgot-password';

    const isRoot = window.location.pathname === '/' || window.location.pathname === '';

    const publicRoutes = [
        '/',
        '/blog',
        '/faq',
        '/cgu',
        '/mentions-legales',
        '/legal',
        '/contact',
        '/features',
        '/glossary',
        '/trade'
    ];

    const isPublicRoute = publicRoutes.some(route => {
        if (route === '/') return window.location.pathname === '/';
        return window.location.pathname.startsWith(route);
    });

    if (isMaintenance && !isAuthRoute) {
        return <Maintenance message={settings?.maintenanceMessage} duration={settings?.maintenanceDuration} />;
    }

    // Skip global auth loading for public routes to speed up LCP/perceived performance
    if (loading && !isRoot && !isPublicRoute) {
        return (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner w-16 h-16 mx-auto mb-4"></div>
                    <p className="text-dark-400">Chargement de StarQuant...</p>
                </div>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <ScrollToTop />
            <React.Suspense fallback={
                <div className="min-h-screen bg-dark-950 flex items-center justify-center">
                    <div className="text-center">
                        <div className="loading-spinner w-16 h-16 mx-auto mb-4"></div>
                    </div>
                </div>
            }>
                <Routes>
                    {/* 1. Protected Routes (Place first to avoid greedy parameterized matches) */}
                    <Route
                        element={
                            user ? <MainLayout /> : <Navigate to="/login" replace />
                        }
                    >
                        <Route path="/dashboard" element={<Workspace />} />
                        <Route path="/strategys" element={<Dashboard />} />
                        <Route path="/fundamental" element={<Fundamental />} />
                        <Route path="/strategy-hub" element={<StrategyHub />} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/metrics" element={<Metrics />} />
                        <Route path="/audit" element={<AIAudit />} />
                        <Route path="/trades" element={<Journal />} />
                        <Route path="/trading-floor" element={<TradingFloor />} />
                        <Route path="/options-desk" element={<OptionsDesk />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/smart-analysis" element={<SmartAnalysis />} />
                    </Route>

                    {/* 2. Routes with optional language prefix (Marketing & Auth)
                        LanguageSync validates the lang param — unsupported codes are caught
                        by Firebase 301 redirects before reaching the React app. */}
                    <Route path="/:lang?">
                        <Route index element={
                            <>
                                <LanguageSync />
                                {user ? <Navigate to="/dashboard" replace /> : <LandingPage />}
                            </>
                        } />
                        <Route path="blog" element={<><LanguageSync /><BlogList /></>} />
                        <Route path="blog/:slug" element={<><LanguageSync /><BlogDetail /></>} />
                        <Route path="faq" element={<><LanguageSync /><FAQ /></>} />
                        <Route path="cgu" element={<><LanguageSync /><Legal type="cgu" /></>} />
                        <Route path="mentions-legales" element={<><LanguageSync /><Legal type="mentions-legales" /></>} />
                        <Route path="legal/:type" element={<><LanguageSync /><Legal /></>} />
                        <Route path="contact" element={<><LanguageSync /><Contact /></>} />
                        <Route path="features" element={<><LanguageSync /><Features /></>} />
                        <Route path="glossary" element={<><LanguageSync /><Glossary /></>} />
                        <Route path="glossary/:slug" element={<><LanguageSync /><Glossary /></>} />
                        <Route path="trade/:id" element={<><LanguageSync /><PublicTrade /></>} />
                        <Route element={<AuthLayout />}>
                            <Route path="login" element={<><LanguageSync /><Login /></>} />
                            <Route path="register" element={<><LanguageSync /><Register /></>} />
                            <Route path="forgot-password" element={<><LanguageSync /><ForgotPassword /></>} />
                        </Route>
                    </Route>

                    {/* 3. Direct Auth Routes (root-level, backward compat) */}
                    <Route element={<AuthLayout />}>
                        <Route path="/login" element={<><LanguageSync /><Login /></>} />
                        <Route path="/register" element={<><LanguageSync /><Register /></>} />
                        <Route path="/forgot-password" element={<><LanguageSync /><ForgotPassword /></>} />
                    </Route>

                    {/* 4. Catch-all: redirect unknown routes to homepage */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </React.Suspense>
        </BrowserRouter>
    );
};

export default App;
